import { useEffect, useState } from "react";
import { MessageCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { PageHead, EmptyState } from "@/components/Bits";
import { ROLE_LABELS } from "@/lib/rbac";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ROLE_LABEL = ROLE_LABELS;

export default function Users() {
  const [users, setUsers] = useState([]);
  const [studios, setStudios] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    mobile: "",
    role: "studio_manager",
    studio_id: "",
  });

  const [roles, setRoles] = useState([]);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => {
    load();
    api.get("/studios").then(({ data }) => setStudios(data));
    api.get("/roles").then(({ data }) => setRoles(data.roles.filter((r) => r.active !== false)));
  }, []);

  const selectedRole = roles.find((r) => r.key === form.role);
  const needsStudio = selectedRole ? selectedRole.scope !== "network" : form.role !== "network_admin";
  const labelFor = (key) => roles.find((r) => r.key === key)?.name || ROLE_LABEL[key] || key;

  const studioName = (id) => studios.find((s) => s.id === id)?.name || "—";

  const submit = async () => {
    try {
      await api.post("/users", { ...form, studio_id: form.studio_id || null });
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", mobile: "", role: "studio_manager", studio_id: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const paged = usePaged(users);

  const resetPin = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-pin`);
      toast.success(data.message || `${u.name}'s PIN has been reset`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const [edit, setEdit] = useState(null);
  const [eform, setEform] = useState({ name: "", mobile: "", role: "", studio_id: "", active: true });
  const [del, setDel] = useState(null);

  const openEdit = (u) => {
    setEform({
      name: u.name || "",
      mobile: u.mobile || "",
      role: u.role,
      studio_id: u.studio_id || "",
      active: u.active !== false,
    });
    setEdit(u);
  };

  const editRole = roles.find((r) => r.key === eform.role);
  const editNeedsStudio = editRole
    ? editRole.scope !== "network"
    : eform.role !== "network_admin" && !!edit?.studio_id;

  const saveEdit = async () => {
    try {
      await api.put(`/users/${edit.id}`, {
        name: eform.name,
        mobile: eform.mobile || null,
        role: eform.role,
        studio_id: editNeedsStudio ? eform.studio_id || null : null,
        active: eform.active,
      });
      toast.success(`${eform.name} updated`);
      setEdit(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${del.id}`);
      toast.success(`${del.name} removed`);
      setDel(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const sendWhatsApp = (u) => {
    const digits = String(u.mobile || "").replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) {
      toast.error("A valid 10-digit mobile number is needed to send instructions");
      return;
    }
    const msg =
      `Hello ${u.name}, your Veloskin Detailer Network account is ready. ` +
      `Open ${window.location.origin} and choose "Studio login". ` +
      `Enter your mobile ${digits} — you'll verify your email and set your own 4-digit PIN on first sign-in.`;
    window.open(`https://wa.me/91${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  return (
    <div>
      <PageHead title="Users" subtitle="Central team, studio managers and PPF installers">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn">
              <Plus className="mr-2 h-4 w-4" /> New user
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>New user</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  data-testid="user-name-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Email {form.role === "network_admin" ? "*" : "(optional)"}</Label>
                <Input
                  data-testid="user-email-input"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="name@example.com"
                />
                {form.role !== "network_admin" && (
                  <p className="mt-1.5 text-xs text-slate-500">
                    Optional — the user provides and verifies their own email, and sets their PIN, on first login.
                  </p>
                )}
              </div>
              <div>
                <Label>Mobile number {form.role !== "network_admin" ? "*" : ""}</Label>
                <Input
                  data-testid="user-mobile-input"
                  inputMode="numeric"
                  className="min-h-[44px]"
                  value={form.mobile}
                  onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                />
                {form.role !== "network_admin" && (
                  <p className="mt-1.5 text-xs text-slate-500">
                    They sign in with this mobile number + the 4-digit PIN they create.
                  </p>
                )}
              </div>
              <div>
                <Label>Password {form.role === "network_admin" ? "*" : "(admin only)"}</Label>
                <Input
                  data-testid="user-password-input"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles
                      .filter((r) => r.key !== "installer")
                      .map((r) => (
                        <SelectItem key={r.key} value={r.key}>
                          {r.name}
                          {r.scope === "network" ? " · central" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              {needsStudio && (
                <div>
                  <Label>Studio *</Label>
                  <Select
                    value={form.studio_id}
                    onValueChange={(v) => setForm((f) => ({ ...f, studio_id: v }))}
                  >
                    <SelectTrigger data-testid="user-studio-select">
                      <SelectValue placeholder="Select studio" />
                    </SelectTrigger>
                    <SelectContent>
                      {studios.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                data-testid="save-user-btn"
                onClick={submit}
                disabled={
                  !form.name ||
                  (form.role === "network_admin" ? (!form.email || !form.password) : !form.mobile) ||
                  (needsStudio && !form.studio_id)
                }
              >
                Create user
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHead>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="users-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Studio</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.total === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="p-0">
                    <EmptyState
                      icon={Plus}
                      testid="users-empty-state"
                      title="No users yet"
                      hint="Add your central team and studio staff — each one gets a mobile + PIN login you can share straight away."
                      action={
                        <Button data-testid="users-empty-add-btn" onClick={() => setOpen(true)}>
                          <Plus className="mr-2 h-4 w-4" /> Add your first user
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              )}
              {paged.visible.map((u) => (
                <TableRow key={u.id} className="hover:bg-brand-tint/60">
                  <TableCell data-label="Name" className="font-medium">
                    {u.name}
                    {u.active === false && (
                      <span
                        className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500"
                        data-testid={`user-inactive-${u.id}`}
                      >
                        Inactive
                      </span>
                    )}
                  </TableCell>
                  <TableCell data-label="Email" className="text-slate-500">{u.email}</TableCell>
                  <TableCell data-label="Mobile" className="text-slate-600">
                    {u.mobile || "—"}
                  </TableCell>
                  <TableCell data-label="Status">
                    {u.role === "network_admin" ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : u.email_verified && u.pin_set ? (
                      <span
                        data-testid={`user-status-${u.id}`}
                        className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                      >
                        Active
                      </span>
                    ) : (
                      <span
                        data-testid={`user-status-${u.id}`}
                        className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                      >
                        Pending setup
                      </span>
                    )}
                  </TableCell>
                  <TableCell data-label="Role">
                    <Badge className="border border-brand-line bg-slate-50 text-slate-700" variant="outline">
                      {labelFor(u.role)}
                    </Badge>
                  </TableCell>
                  <TableCell data-label="Studio" className="text-slate-500">
                    {u.studio_id ? studioName(u.studio_id) : "Network"}
                  </TableCell>
                  <TableCell data-label="" className="text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      {u.role !== "network_admin" && u.mobile && (
                        <Button
                          size="sm"
                          className="min-h-[44px] bg-[#25D366] text-white hover:bg-[#1da851]"
                          data-testid={`send-whatsapp-${u.id}`}
                          onClick={() => sendWhatsApp(u)}
                        >
                          <MessageCircle className="mr-1.5 h-4 w-4" /> Send via WhatsApp
                        </Button>
                      )}
                      {u.role !== "network_admin" && u.mobile && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="min-h-[44px]"
                          data-testid={`reset-pin-${u.id}`}
                          onClick={() => resetPin(u)}
                        >
                          Reset PIN
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="min-h-[44px]"
                        data-testid={`edit-user-${u.id}`}
                        onClick={() => openEdit(u)}
                      >
                        <Pencil className="mr-1.5 h-4 w-4" /> Edit
                      </Button>
                      {u.role !== "network_admin" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="min-h-[44px] border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => setDel(u)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ShowMore paged={paged} testid="users-show-more" />
        </CardContent>
      </Card>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit {edit?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                className="min-h-[44px]"
                data-testid="edit-user-name-input"
                value={eform.name}
                onChange={(e) => setEform((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Mobile number</Label>
              <Input
                className="min-h-[44px]"
                inputMode="numeric"
                data-testid="edit-user-mobile-input"
                value={eform.mobile}
                onChange={(e) => setEform((f) => ({ ...f, mobile: e.target.value }))}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                Changing the mobile number keeps the user's existing PIN. Use "Reset PIN" if they
                need to set a new one.
              </p>
            </div>
            {edit?.role !== "network_admin" && (
              <div>
                <Label>Role</Label>
                <Select
                  value={eform.role}
                  onValueChange={(v) => setEform((f) => ({ ...f, role: v }))}
                >
                  <SelectTrigger data-testid="edit-user-role-select" className="min-h-[44px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.key} value={r.key}>
                        {r.name}
                        {r.scope === "network" ? " · central" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {edit?.role !== "network_admin" && editNeedsStudio && (
              <div>
                <Label>Studio *</Label>
                <Select
                  value={eform.studio_id}
                  onValueChange={(v) => setEform((f) => ({ ...f, studio_id: v }))}
                >
                  <SelectTrigger data-testid="edit-user-studio-select" className="min-h-[44px]">
                    <SelectValue placeholder="Select studio" />
                  </SelectTrigger>
                  <SelectContent>
                    {studios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {edit?.role !== "network_admin" && (
              <div>
                <Label>Access</Label>
                <div className="mt-1.5 flex gap-2">
                  {[
                    [true, "Active"],
                    [false, "Suspended"],
                  ].map(([v, label]) => (
                    <button
                      key={label}
                      data-testid={`edit-user-active-${v}`}
                      onClick={() => setEform((f) => ({ ...f, active: v }))}
                      className={`min-h-[44px] rounded px-4 text-sm font-medium transition-colors duration-200 ${
                        eform.active === v
                          ? "bg-brand text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-xs text-slate-500">
                  A suspended user keeps their record but cannot sign in.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setEdit(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px]"
              data-testid="save-edit-user-btn"
              onClick={saveEdit}
              disabled={!eform.name || (editNeedsStudio && !eform.studio_id)}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Remove {del?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {del?.name} will lose access immediately and disappear from this list. Jobs, customers
            and reports they created are kept. To keep their record but block sign-in, use Edit
            and set them to Suspended instead.
          </p>
          <DialogFooter>
            <Button variant="outline" className="min-h-[44px]" onClick={() => setDel(null)}>
              Cancel
            </Button>
            <Button
              className="min-h-[44px] bg-rose-600 text-white hover:bg-rose-700"
              data-testid="confirm-delete-user-btn"
              onClick={confirmDelete}
            >
              Delete user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
